import React, { useEffect, useState, useRef, useCallback } from "react";

import getAnnouncementById from "../../../api/getAnnouncementById";
import { formatPrice } from "../../../utils/formatPrice";

import sendPaperIcon from "../../../assets/icons/paperplane.svg";
import fileIcon from "../../../assets/icons/paperclip.svg";
import user_profile_photo from "../../images/def_user_icon.svg";
import menu_dot from '../../../assets/icons/dots_three_outline_vertical.svg';
import backicon from '../../../assets/icons/backicon.svg';
import checkIcon from '../../../assets/icons/check.svg';
import checksIcon from '../../../assets/icons/checks.svg';
import { t } from "i18next";

const BOT_USER_ID = 1003;

const MessageStatus = ({ message, currentUserId }) => {
  if (message.sender_id !== currentUserId) return null;
  const isRead = message.is_read || message.isRead || false;
  return (
    <div className="flex items-center ml-1">
      <img
        src={isRead ? checksIcon : checkIcon}
        alt={isRead ? "Прочитано" : "Отправлено"}
        className={`w-3.5 h-3.5 ${isRead ? "opacity-80" : "opacity-50"}`}
      />
    </div>
  );
};

const AnnouncementMessage = ({ url }) => {
  const [announcementData, setAnnouncementData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAnnouncement = async () => {
      setLoading(true);
      setError(null);
      try {
        const parts = url.split('/');
        const announcementId = parts[parts.length - 1];
        if (announcementId) {
          const data = await getAnnouncementById(announcementId);
          setAnnouncementData(data.announcement);
        } else {
          setError("Invalid URL");
        }
      } catch (err) {
        setError("Failed to load");
      } finally {
        setLoading(false);
      }
    };
    fetchAnnouncement();
  }, [url]);

  if (loading) {
    return (
      <div className="w-full p-3 text-center bg-white border border-gray-200 rounded-xl animate-pulse text-gray-400 text-xs">
        {t('loading_announcement')}
      </div>
    );
  }

  if (error || !announcementData) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="block w-full p-3 bg-red-50 border border-red-200 rounded-xl text-center">
        <p className="text-red-500 text-xs">{t('error_loading_announcement')}</p>
        <p className="text-xs text-gray-400 truncate mt-1">{url}</p>
      </a>
    );
  }

  const imageUrl =
    (announcementData.images && announcementData.images.length > 0 && announcementData.images[0].image_url) ||
    (announcementData.image_urls && announcementData.image_urls.length > 0 && announcementData.image_urls[0]) ||
    announcementData.image_url ||
    null;

  const priceDisplay = formatPrice(
    announcementData.price,
    announcementData.price_type,
    announcementData.is_negotiable,
    announcementData.barter
  );

  const locationDisplay = announcementData.location
    ? typeof announcementData.location === 'string'
      ? announcementData.location
      : announcementData.location.name || announcementData.location.city || ''
    : '';

  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="block no-underline group">
      <div className="flex gap-3 p-3 bg-white border border-gray-200 rounded-xl transition-all group-hover:border-[#147F26] group-hover:shadow-sm min-w-[220px]">
        {imageUrl && (
          <img
            src={imageUrl}
            alt="Announcement"
            className="w-16 h-16 object-cover rounded-lg bg-gray-100 flex-shrink-0"
          />
        )}
        <div className="flex-1 flex flex-col justify-between min-w-0">
          <div>
            <h4 className="text-xs font-semibold text-gray-800 truncate mb-1">
              {announcementData.title || t('announcement')}
            </h4>
            <div className="text-sm font-bold text-[#147F26] mb-1">{priceDisplay}</div>
            {locationDisplay && (
              <div className="flex items-center gap-1 text-[10px] text-gray-400">
                <span>📍</span>
                <span className="truncate">{locationDisplay}</span>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
            <span className="text-[10px] font-medium text-[#147F26]">{t('viewDetails')}</span>
            <span className="text-[#147F26] text-xs group-hover:translate-x-0.5 transition-transform">→</span>
          </div>
        </div>
      </div>
    </a>
  );
};

const ChatConversation = ({ chat, currentUserId, socketInstance, closeModal, isReadOnly = false }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const messagesContainerRef = useRef(null);

  const currentRoomName = chat?.room_name ? chat.room_name : chat?.room_id ? `chat_${chat.room_id}` : null;

  const markChatAsRead = useCallback(() => {
    if (chat?.room_id && socketInstance) {
      socketInstance.emit("mark_chat_as_read", { chat_id: chat.room_id });
    }
  }, [chat?.room_id, socketInstance]);

  useEffect(() => {
    if (socketInstance && chat?.room_id) {
      socketInstance.emit("join_chat", { other_user_id: chat.other_user_id });

      const handleJoinChatSuccess = (data) => {
        setMessages(data.messages || []);
        markChatAsRead();
      };

      const handleChatMessage = (data) => {
        setMessages((prevMessages) => {
          const isDuplicate = prevMessages.some(
            (msg) =>
              msg.sender_id === data.sender_id &&
              msg.message === data.message &&
              Math.abs(new Date(msg.timestamp).getTime() - new Date(data.timestamp).getTime()) < 1000
          );
          return isDuplicate ? prevMessages : [...prevMessages, data];
        });
      };

      socketInstance.on("join_chat_success", handleJoinChatSuccess);
      socketInstance.on("chat_message", handleChatMessage);

      return () => {
        socketInstance.off("join_chat_success", handleJoinChatSuccess);
        socketInstance.off("chat_message", handleChatMessage);
      };
    } else {
      setMessages([]);
    }
  }, [socketInstance, chat, currentRoomName, markChatAsRead]);

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSendMessage = () => {
    if (newMessage.trim() && currentRoomName) {
      socketInstance.emit("chat_message", {
        room_name: currentRoomName,
        message: newMessage,
      });
      const optimisticMessage = {
        tempId: Date.now() + Math.random(),
        message: newMessage,
        sender_id: currentUserId,
        sender_name: "Вы",
        timestamp: new Date().toISOString(),
        is_read: false,
        isRead: false,
      };
      setMessages((prev) => [...prev, optimisticMessage]);
      setNewMessage("");
    }
  };

  const groupMessagesByDate = (msgs) => {
    return msgs.reduce((groups, message) => {
      const messageDate = new Date(message.timestamp);
      if (isNaN(messageDate.getTime())) return groups;
      const isoKey = messageDate.toISOString().split('T')[0];
      if (!groups[isoKey]) groups[isoKey] = [];
      groups[isoKey].push(message);
      return groups;
    }, {});
  };

  const groupedMessages = groupMessagesByDate(messages);

  const formatDateHeader = (isoDateKey) => {
    const [y, m, d] = (isoDateKey || '').split('-').map(Number);
    const messageDate = new Date(y, (m || 1) - 1, d || 1);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const daysOfWeek = t('daysOfWeek', { returnObjects: true });
    const months = t('months', { returnObjects: true });

    if (messageDate.toDateString() === today.toDateString()) return t('today');
    if (messageDate.toDateString() === yesterday.toDateString()) return t('yesterday');
    if (messageDate > new Date(today.setDate(today.getDate() - today.getDay()))) return daysOfWeek[messageDate.getDay()];
    if (messageDate.getFullYear() === today.getFullYear()) return `${messageDate.getDate()} ${months[messageDate.getMonth()]}`;
    return isoDateKey;
  };

  const isAnnouncementUrl = (messageText) => /^https:\/\/agroland\.kz\/announcement\/\d+$/.test(messageText);

  const chatPartnerName = chat
    ? (chat.sender_id === currentUserId ? chat.receiver_name : chat.sender_name)
    : t('select_chat_placeholder');

  return (
    <div className="flex flex-col h-full overflow-hidden bg-white">

      {/* Header */}
      <header className="flex h-16 flex-shrink-0 items-center justify-between px-4 border-b border-gray-100 bg-white sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <img
            src={backicon}
            alt="Назад"
            className="w-5 h-5 cursor-pointer md:hidden"
            onClick={closeModal}
          />
          <div className="relative">
            <img
              src={user_profile_photo}
              alt={chatPartnerName}
              className="w-10 h-10 rounded-full object-cover"
            />
            <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-[#147F26] border-2 border-white rounded-full" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 text-sm leading-tight">{chatPartnerName}</h3>
          </div>
        </div>
        <button className="p-2 rounded-full hover:bg-gray-100 transition-colors">
          <img src={menu_dot} alt="Меню" className="w-5 h-5 opacity-60" />
        </button>
      </header>

      {/* Messages area */}
      <div
        ref={messagesContainerRef}
        className="flex-1 min-h-0 overflow-y-auto px-4 py-4 bg-gray-50 space-y-4"
      >
        {Object.keys(groupedMessages).map((date) => (
          <div className="flex flex-col space-y-2" key={date}>

            {/* Date separator */}
            <div className="flex items-center justify-center my-2">
              <span className="text-xs text-gray-400 bg-gray-50 px-2">
                {formatDateHeader(date)}
              </span>
            </div>

            {groupedMessages[date].map((msg, index) => {
              const isSentByCurrentUser = msg.sender_id === currentUserId;
              const isBotMessage = msg.sender_id === BOT_USER_ID;

              const rawText = msg.message || "";
              const displayText = isBotMessage
                ? rawText.replace(/^🤖\s*Agroland\s*Гид:\s*/i, "")
                : rawText;

              return (
                <div
                  key={msg.tempId || index}
                  className={`flex ${isSentByCurrentUser ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[70%] md:max-w-[55%] px-3 py-2 rounded-2xl text-sm
                      ${isSentByCurrentUser
                        ? "bg-[#147F26] text-white rounded-br-sm"
                        : "bg-white text-gray-800 border border-gray-100 rounded-bl-sm shadow-sm"}
                      ${isBotMessage ? "bg-amber-50 border border-amber-200 text-gray-800" : ""}
                    `}
                  >
                    {isAnnouncementUrl(displayText) ? (
                      <AnnouncementMessage url={displayText} />
                    ) : (
                      <span className="whitespace-pre-wrap break-words leading-relaxed">{displayText}</span>
                    )}

                    <div className={`flex items-center gap-0.5 text-[10px] mt-1
                      ${isSentByCurrentUser ? "justify-end text-white/70" : "justify-start text-gray-400"}
                    `}>
                      <time>
                        {new Date(msg.timestamp).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </time>
                      {isSentByCurrentUser && <MessageStatus message={msg} currentUserId={currentUserId} />}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Input area */}
      {!isReadOnly && (
        <div className="px-4 py-3 bg-white border-t border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <button className="p-2 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0">
              <img src={fileIcon} alt="Файл" className="w-5 h-5 opacity-40" />
            </button>

            <input
              type="text"
              className="flex-1 bg-gray-50 border border-gray-200 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-[#147F26] placeholder-gray-400 text-gray-700 transition-colors"
              placeholder={t('write_message_placeholder')}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") handleSendMessage();
              }}
            />

            <button
              className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-all
                ${newMessage.trim()
                  ? "bg-[#147F26] hover:bg-[#0f5f1a] active:scale-95"
                  : "bg-gray-200 cursor-not-allowed"}`}
              onClick={handleSendMessage}
              disabled={!newMessage.trim()}
            >
              <img
                src={sendPaperIcon}
                alt="Отправить"
                className={`w-4 h-4 ${newMessage.trim() ? "brightness-0 invert" : "opacity-40"}`}
              />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatConversation;
